import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | {
      type: "address";
      lines: string[];
      email: string;
      phone: string;
    };

const SECTIONS: { title: string; blocks: Block[] }[] = [
  {
    title: "1. Welcome",
    blocks: [
      {
        type: "p",
        text: 'These Terms of Use ("Terms") govern your access to and use of the website, mobile applications, and related services (collectively, the "Service") provided by Trevica LTD ("Company", "we", "our", or "us").',
      },
      {
        type: "address",
        lines: ["Registered Address", "307 Cotton Exchange", "Old Hall Street", "Liverpool", "L3 9LQ", "United Kingdom"],
        email: "support@trevicatech.com",
        phone: "+44 7451 257027",
      },
      {
        type: "p",
        text: "By accessing or using the Service, you confirm that you have read, understood, and agreed to these Terms, together with our Privacy Policy. If you do not agree with any part of these Terms, you should discontinue use of the Service.",
      },
    ],
  },
  {
    title: "2. Definitions",
    blocks: [
      { type: "p", text: "For the purposes of these Terms:" },
      {
        type: "list",
        items: [
          '"Company" means Trevica LTD.',
          '"Service" refers to our website, applications, digital products, and any related functionality made available by the Company.',
          '"User", "you", or "your" refers to any individual accessing or using the Service.',
          '"Content" includes all software, text, graphics, images, logos, designs, documentation, and other materials available through the Service.',
        ],
      },
    ],
  },
  {
    title: "3. Eligibility",
    blocks: [
      { type: "p", text: "You may use the Service only if:" },
      {
        type: "list",
        items: [
          "You are at least 18 years of age or have reached the legal age required in your country of residence.",
          "You have the legal authority to enter into these Terms.",
          "Your use of the Service complies with all applicable laws and regulations.",
        ],
      },
    ],
  },
  {
    title: "4. Acceptable Use",
    blocks: [
      { type: "p", text: "You agree to use the Service responsibly and only for lawful purposes." },
      { type: "p", text: "You must not:" },
      {
        type: "list",
        items: [
          "Use the Service in violation of applicable laws or regulations.",
          "Attempt to gain unauthorized access to any part of the Service or its infrastructure.",
          "Interfere with the security, availability, or functionality of the Service.",
          "Introduce malware, harmful code, or other technologies intended to damage the Service.",
          "Use automated software, bots, or scraping tools without our prior written permission.",
          "Engage in activities that could disrupt the experience of other users.",
        ],
      },
    ],
  },
  {
    title: "5. Purchases and Subscriptions",
    blocks: [
      { type: "p", text: "Certain features of the Service may require payment or an active subscription." },
      { type: "p", text: "Where applicable:" },
      {
        type: "list",
        items: [
          "Payments are processed through secure third-party payment providers.",
          "Subscription plans may renew automatically unless canceled before the renewal date.",
          "You are responsible for managing or canceling your subscription before renewal if you no longer wish to continue.",
          "Refunds are governed by our separate Refund Policy.",
        ],
      },
      { type: "p", text: "The Company does not store your full payment card details." },
    ],
  },
  {
    title: "6. Ownership and Intellectual Property",
    blocks: [
      {
        type: "p",
        text: "All intellectual property rights relating to the Service, including its design, software, trademarks, branding, graphics, and written materials, remain the exclusive property of the Company or its licensors.",
      },
      {
        type: "p",
        text: "You are granted a limited, personal, non-exclusive, non-transferable, and revocable license to access and use the Service for personal, non-commercial purposes.",
      },
      { type: "p", text: "Without our prior written consent, you may not:" },
      {
        type: "list",
        items: [
          "Copy, reproduce, or distribute any part of the Service.",
          "Modify, reverse engineer, or create derivative works.",
          "Remove copyright, trademark, or proprietary notices.",
          "Use our trademarks or branding in any way that suggests endorsement or affiliation.",
        ],
      },
    ],
  },
  {
    title: "7. User Communications",
    blocks: [
      {
        type: "p",
        text: "If you contact us, submit feedback, suggestions, or other communications through the Service:",
      },
      {
        type: "list",
        items: [
          "You retain ownership of your submissions.",
          "You grant the Company the right to use such communications solely for improving, operating, and supporting the Service.",
          "You confirm that your submissions do not infringe the rights of any third party.",
        ],
      },
    ],
  },
  {
    title: "8. Third-Party Services",
    blocks: [
      { type: "p", text: "The Service may contain links to third-party websites or integrate with external services." },
      { type: "p", text: "We do not control and are not responsible for:" },
      {
        type: "list",
        items: [
          "Third-party websites or applications.",
          "Their content, privacy practices, or availability.",
          "Any products or services provided by third parties.",
        ],
      },
      { type: "p", text: "Your interactions with third-party services are governed by their own terms and policies." },
    ],
  },
  {
    title: "9. Disclaimer",
    blocks: [
      { type: "p", text: 'The Service is provided on an "as available" and "as is" basis.' },
      { type: "p", text: "To the maximum extent permitted by law, the Company makes no guarantees regarding:" },
      {
        type: "list",
        items: [
          "Continuous or uninterrupted availability.",
          "Accuracy, reliability, or completeness of information.",
          "Compatibility with every device or operating system.",
          "Achievement of any particular results through use of the Service.",
        ],
      },
    ],
  },
  {
    title: "10. Limitation of Liability",
    blocks: [
      {
        type: "p",
        text: "To the fullest extent permitted by applicable law, Trevica LTD shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, revenue, profits, or business opportunities arising from your use of, or inability to use, the Service.",
      },
      {
        type: "p",
        text: "Nothing in these Terms limits liability where such limitation is prohibited under applicable law.",
      },
    ],
  },
  {
    title: "11. Your Responsibilities",
    blocks: [
      { type: "p", text: "As a user of the Service, you are responsible for:" },
      {
        type: "list",
        items: [
          "Maintaining the security of your devices and credentials.",
          "Providing accurate information where requested.",
          "Ensuring your use of the Service complies with these Terms.",
          "Any activity carried out through your access to the Service.",
        ],
      },
    ],
  },
  {
    title: "12. Privacy",
    blocks: [
      {
        type: "p",
        text: "Our collection and use of personal information are described in our Privacy Policy, which forms an integral part of these Terms.",
      },
      {
        type: "p",
        text: "By using the Service, you acknowledge that your personal information may be processed as described in that Policy.",
      },
    ],
  },
  {
    title: "13. Updates to the Service",
    blocks: [
      {
        type: "p",
        text: "We may improve, modify, suspend, or discontinue any feature or functionality of the Service without prior notice where reasonably necessary.",
      },
      { type: "p", text: "We also reserve the right to revise these Terms periodically." },
      {
        type: "p",
        text: "The most recent version will always be available on the Site, and continued use of the Service constitutes acceptance of any revised Terms.",
      },
    ],
  },
  {
    title: "14. Suspension or Termination",
    blocks: [
      { type: "p", text: "We may suspend or terminate access to the Service immediately if:" },
      {
        type: "list",
        items: [
          "These Terms are violated.",
          "Required by applicable law.",
          "Necessary to protect the Company, the Service, or other users.",
          "Fraudulent, abusive, or unauthorized activity is suspected.",
        ],
      },
    ],
  },
  {
    title: "15. Governing Law",
    blocks: [
      {
        type: "p",
        text: "These Terms shall be governed by and interpreted in accordance with the laws of England and Wales.",
      },
      {
        type: "p",
        text: "Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales, unless applicable consumer protection laws provide otherwise.",
      },
    ],
  },
  {
    title: "16. Severability",
    blocks: [
      {
        type: "p",
        text: "If any provision of these Terms is found to be unlawful or unenforceable, the remaining provisions shall remain valid and enforceable to the fullest extent permitted by law.",
      },
    ],
  },
  {
    title: "17. Entire Agreement",
    blocks: [
      {
        type: "p",
        text: "These Terms, together with our Privacy Policy and any other policies expressly incorporated by reference, constitute the complete agreement between you and the Company regarding your use of the Service.",
      },
    ],
  },
  {
    title: "18. Contact Us",
    blocks: [
      { type: "p", text: "If you have any questions regarding these Terms or the Service, please contact us:" },
      {
        type: "address",
        lines: ["Trevica LTD", "307 Cotton Exchange", "Old Hall Street", "Liverpool", "L3 9LQ", "United Kingdom"],
        email: "support@trevicatech.com",
        phone: "+44 7451 257027",
      },
    ],
  },
];

function SectionBlock({ block }: { block: Block }) {
  if (block.type === "p") {
    return (
      <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-3">
        {block.text}
      </p>
    );
  }

  if (block.type === "list") {
    return (
      <ul className="mt-3 space-y-2 list-disc pl-5">
        {block.items.map((item, i) => (
          <li key={i} className="text-sm md:text-base text-ink-700 leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="mt-3 text-sm md:text-base text-ink-700 leading-relaxed">
      <p className="font-semibold text-ink-950">{block.lines[0]}</p>
      {block.lines.slice(1).map((line, i) => (
        <p key={i}>{line}</p>
      ))}
      <p className="mt-2">
        Email: <span className="font-medium text-ink-950">{block.email}</span>
      </p>
      <p>
        Phone: <span className="font-medium text-ink-950">{block.phone}</span>
      </p>
    </div>
  );
}

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-cream-100 border-b border-ink-950/5">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-950 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back
            </button>
            <p className="text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-ink-500 mb-4">
              Last updated: July 2026
            </p>
            <h1 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Terms of Use
            </h1>
            <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed max-w-2xl">
              Please read these Terms of Use carefully before using the Trevica LTD website and services.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="relative w-full py-16 md:py-24 bg-cream-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="space-y-10 md:space-y-14">
              {SECTIONS.map((section) => (
                <div key={section.title}>
                  <h2 className="font-display font-bold text-xl md:text-2xl text-ink-950 mb-4">
                    {section.title}
                  </h2>
                  {section.blocks.map((block, i) => (
                    <SectionBlock key={i} block={block} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
